import { useEffect, useMemo, useRef, useState } from 'react'

import { ReactionDiffusionBackground } from './components/ReactionDiffusionBackground'
import { TuningPanel } from './components/TuningPanel'
import { DEFAULT_PRESET, presetById, type Chemistry, type Material } from './gl/presets'
import type { ReactionDiffusionEngine } from './gl/simulation'

type Client = { name: string; slug: string; badge?: boolean }

// Drop transparent PNGs into public/logos/<slug>.png. Any colour works — the
// grid forces them white. Until a file lands, the wordmark stands in.
const CLIENTS: Client[] = [
  { name: 'Microsoft', slug: 'microsoft' },
  { name: 'Mercedes-Benz', slug: 'mercedes-benz', badge: true },
  { name: 'BMW', slug: 'bmw', badge: true },
  { name: 'Adidas', slug: 'adidas', badge: true },
  { name: 'NVIDIA', slug: 'nvidia' },
  { name: 'Intel', slug: 'intel' },
  { name: 'Smithsonian', slug: 'smithsonian' },
  { name: 'Harry Potter', slug: 'harry-potter' },
  { name: 'Avatar', slug: 'avatar' },
  { name: 'Coachella', slug: 'coachella' },
  { name: 'Bill \u0026 Melinda Gates Foundation', slug: 'gates-foundation' },
  { name: 'Alex Grey', slug: 'alex-grey' },
  { name: 'Android Jones', slug: 'android-jones' },
  { name: 'HP', slug: 'hp', badge: true },
  { name: 'Jeep', slug: 'jeep' },
  { name: 'Discovery Channel', slug: 'discovery' },
  { name: 'San Francisco 49ers', slug: '49ers', badge: true },
  { name: 'Pittsburgh Steelers', slug: 'steelers', badge: true },
  { name: 'MSN', slug: 'msn' },
  { name: 'HTC', slug: 'htc' },
  { name: 'PBS Kids', slug: 'pbs-kids', badge: true },
  { name: 'San Diego Chargers', slug: 'chargers', badge: true },
  { name: 'Microdose VR', slug: 'microdose', badge: true },
  { name: 'CHROMA', slug: 'chroma' },
  { name: 'Vision Agency', slug: 'vision-agency' },
  { name: 'Logitech', slug: 'logitech' },
  { name: 'ViewSonic', slug: 'viewsonic' },
  { name: 'Kyocera', slug: 'kyocera' },
]

/**
 * Wide layouts put the content in a left-hand lane and the mark out to the
 * right, so the two never sit on top of each other. Narrow ones have no room
 * for that, so the mark goes back to centre and simply sits behind.
 */
const WIDE_LAYOUT = '(min-width: 60rem)'

/** Chord that reveals the tuning workbench. Escape hides it again. */
function useTuningPanel(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      // event.key is already case-folded by the shift, so compare on the
      // physical key — otherwise a different layout would never match.
      if (event.shiftKey && event.code === 'KeyT') {
        event.preventDefault()
        setOpen((value) => !value)
      } else if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return [open, setOpen]
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  )

  useEffect(() => {
    const media = window.matchMedia(query)
    const update = () => setMatches(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [query])

  return matches
}

/**
 * The address is never written into the markup. Scrapers pull the served HTML
 * and the rendered DOM, so it exists only as an encoded string until someone
 * actually clicks — which is also why this is a button rather than an anchor:
 * an href would put it right back in the document.
 */
const CONTACT = 'c3BvcmVnYW5pY2xpdmVAZ21haWwuY29t'

function ReachOut() {
  return (
    <button
      type="button"
      className="contact"
      aria-label="Reach out by email"
      onClick={() => {
        window.location.href = `mailto:${atob(CONTACT)}`
      }}
    >
      <span>reach out</span>
      <span className="contact__arrow" aria-hidden="true">&rarr;</span>
    </button>
  )
}

function ClientLogo({ client }: { client: Client }) {
  const [failed, setFailed] = useState(false)

  if (failed) {
    return <span className="clients__wordmark">{client.name}</span>
  }

  return (
    <img
      className={`clients__logo${client.badge ? ' clients__logo--badge' : ''}`}
      src={`/logos/${client.slug}.png`}
      alt={client.name}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  )
}

export default function App() {
  const engineRef = useRef<ReactionDiffusionEngine | null>(null)
  const wide = useMediaQuery(WIDE_LAYOUT)
  const [tuning] = useTuningPanel()

  const [presetId, setPresetId] = useState(DEFAULT_PRESET.id)
  const [materialPatch, setMaterialPatch] = useState<Partial<Material>>({})
  const [chemistryPatch, setChemistryPatch] = useState<Partial<Chemistry>>({})

  const preset = useMemo(() => {
    const base = presetById(presetId)
    return {
      ...base,
      material: { ...base.material, ...materialPatch },
      chemistry: { ...base.chemistry, ...chemistryPatch },
    }
  }, [presetId, materialPatch, chemistryPatch])

  const selectPreset = (id: string) => {
    setPresetId(id)
    // Per-preset tuning is a starting point, not something to carry across.
    setChemistryPatch({})
  }

  const restoreDefaults = () => {
    setMaterialPatch({})
    setChemistryPatch({})
  }

  return (
    <>
      <ReactionDiffusionBackground
        preset={preset}
        className="backdrop"
        maskImage="/logo.png"
        maskCoverage={wide ? 0.38 : 0.5}
        maskCenterX={wide ? 0.76 : 0.74}
        maskCenterY={wide ? 0.46 : 0.17}
        maskMode="block"
        engineRef={engineRef}
      />

      <main className="page">
        <header className="masthead">
          <p className="eyebrow">Scott Hedstrom</p>
          <h1 className="title">
            <span className="title__accent">making </span>
            <span className="title__magic">magic.</span>
          </h1>
        </header>

        <section className="intro">
          <p className="intro__lead">
            A lifetime of creative engineering: digital platforms,
            virtual worlds, immersive installations, and all the software
            powering it.
          </p>
          <ReachOut />
        </section>

        <section className="clients">
          <h2 className="clients__heading">Previous collaborators</h2>
          <ul className="clients__grid">
            {CLIENTS.map((client) => (
              <li className="clients__cell" key={client.slug}>
                <ClientLogo client={client} />
              </li>
            ))}
          </ul>
        </section>

      </main>

      {tuning && (
        <TuningPanel
          preset={preset}
          presetId={presetId}
          onPresetChange={selectPreset}
          onMaterialChange={(patch) => setMaterialPatch((current) => ({ ...current, ...patch }))}
          onChemistryChange={(patch) => setChemistryPatch((current) => ({ ...current, ...patch }))}
          onReset={() => engineRef.current?.reset()}
          onClear={restoreDefaults}
        />
      )}
    </>
  )
}
